import classNames from 'classnames';
import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';

import { isCcpaBannerVariantActive } from '../consent-manager/flags';
import { useConsentLanguage } from '../consent-manager/useConsentLanguageHook';
import CCpaBanner from '@wetransfer/transfer/component/ccpa';
import {
  Intro,
  AcceptButton,
  SaveButton,
  RejectButton,
  PurposeList,
  VendorList,
} from '@wetransfer/transfer/component/consent-manager';
import Button from '@wetransfer/transfer/component/form/element/button';
import { consentTracker } from '@wetransfer/transfer/modules/consent-manager';
import I18n from '@wetransfer/transfer/modules/i18n';
import Actions from '@wetransfer/transfer/store/action';
import { State } from '@wetransfer/transfer/store/types';

import { CookieWallVariation } from './cookie-wall-variations-util';

import { isUrlPricingPage } from './index';

export interface Props {
  closePanel(): void;
  language: string;
  cookieWallVariation: CookieWallVariation;
  showConsentWallPreferences: boolean;
  isPanelOpen: boolean;
  setShowConsentWallPreferences(value: boolean): void;
}

export const ConsentWall: React.FC<Props> = ({
  closePanel,
  language,
  cookieWallVariation,
  showConsentWallPreferences,
  setShowConsentWallPreferences,
  isPanelOpen,
}) => {
  const consentLanguage = useConsentLanguage(language);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isPanelOpen) {
      dispatch(Actions.Consent.setUIVisible(true));
    }
    consentTracker.showModal(cookieWallVariation.trackingCategory);
  }, [cookieWallVariation.trackingCategory, dispatch, isPanelOpen]);

  const handlePrivacyPrefClick = () => {
    setShowConsentWallPreferences(true);
    consentTracker.clickPrivacyPreferences(
      cookieWallVariation.trackingCategory
    );
  };

  // On the pricing page, we return a variant of the CCPA banner
  // for IP addresses in MX, BR, and CA
  if (isCcpaBannerVariantActive() && isUrlPricingPage()) {
    return <CCpaBanner />;
  }

  return (
    <div
      className={classNames('welcome__content welcome__cookie-notice', {
        'welcome__cookie-notice-full-height': showConsentWallPreferences,
      })}
    >
      <>
        <div className="welcome__title">
          <div>{I18n.t('transfer.consent-manager.art.title-small')}</div>
        </div>
        <div
          className={cookieWallVariation.titleClass}
          dangerouslySetInnerHTML={{
            __html: I18n.t(cookieWallVariation.tTitle),
          }}
        />
      </>
      {showConsentWallPreferences ? (
        <React.Fragment>
          <Intro cookieWallVariation={cookieWallVariation}>
            <AcceptButton
              onClick={closePanel}
              context="intro_preference"
              cookieWallVariant={cookieWallVariation.trackingCategory}
            />
            <RejectButton
              onClick={closePanel}
              context="intro_preference"
              cookieWallVariant={cookieWallVariation.trackingCategory}
              additionalClass="welcome__button--decline-experiment"
            />
            <SaveButton
              text={I18n.t('transfer.consent-manager.button-save-and-exit')}
              onClick={closePanel}
              context="intro_preference"
              cookieWallVariant={cookieWallVariation.trackingCategory}
            />
          </Intro>

          <div className="welcome__consent-preferences">
            <div className="welcome__purpose-list">
              <div className="welcome__cookie-notice-title">
                {I18n.t('transfer.consent-manager.preferences-headline')}
              </div>
              <div
                className="welcome__cookie-notice-text-experiment"
                dangerouslySetInnerHTML={{
                  __html: I18n.t(
                    'transfer.consent-manager.tos-experiment.cookie-text'
                  ),
                }}
              />
              <div
                className="welcome__cookie-notice-text-experiment"
                dangerouslySetInnerHTML={{
                  __html: I18n.t(
                    'transfer.consent-manager.preferences-text-allow-cookies'
                  ),
                }}
              />
              <PurposeList
                consentLanguage={consentLanguage}
                isWhiteText={true}
              />
            </div>

            <div className="welcome__buttons ">
              <AcceptButton
                onClick={closePanel}
                context="purpose_preference"
                cookieWallVariant={cookieWallVariation.trackingCategory}
              />
              <SaveButton
                text={I18n.t('transfer.consent-manager.button-save-and-exit')}
                onClick={closePanel}
                context="purpose_preference"
              />
              <Button
                text={I18n.t('transfer.consent-manager.button-reject')}
                className="welcome__button welcome__button--decline welcome__button--hidden"
              />
            </div>

            <div className="welcome__cookie-notice-title welcome__consent-title">
              {I18n.t('transfer.consent-manager.partners-headline')}
            </div>
            <div
              className="welcome__cookie-notice-text-experiment"
              dangerouslySetInnerHTML={{
                __html: I18n.t('transfer.consent-manager.partners-text'),
              }}
            />

            <VendorList consentLanguage={consentLanguage} isWhiteText={true} />

            <div className="welcome__buttons welcome__buttons-vendor-list">
              <AcceptButton
                onClick={closePanel}
                context="vendor_preference"
                cookieWallVariant={cookieWallVariation.trackingCategory}
              />
              <SaveButton
                text={I18n.t('transfer.consent-manager.button-save-and-exit')}
                onClick={closePanel}
                context="vendor_preference"
                cookieWallVariant={cookieWallVariation.trackingCategory}
              />
              <Button
                text={I18n.t('transfer.consent-manager.button-reject')}
                className="welcome__button welcome__button--decline welcome__button--hidden"
              />
            </div>
          </div>
        </React.Fragment>
      ) : (
        <Intro cookieWallVariation={cookieWallVariation}>
          <AcceptButton
            onClick={closePanel}
            context="intro_splash"
            cookieWallVariant={cookieWallVariation.trackingCategory}
          />
          <Button
            text={I18n.t('transfer.consent-manager.manage-cookies')}
            className="welcome__button welcome__button--preferences"
            onClick={handlePrivacyPrefClick}
          />
          <RejectButton
            onClick={closePanel}
            context="intro_splash"
            cookieWallVariant={cookieWallVariation.trackingCategory}
            additionalClass="welcome__button--decline-experiment"
          />
        </Intro>
      )}
    </div>
  );
};

const mapStateToProps = ({ session, ui, route }: State) => ({
  language: session.language,
  region: session.region,
  showConsentWallPreferences: ui.showConsentWallPreferences,
  isPanelOpen: route.isPanelOpen,
});

const mapDispatchToProps = {
  setShowConsentWallPreferences: Actions.UI.setShowConsentWallPreferences,
};

export const ConnectedConsentWall = connect(
  mapStateToProps,
  mapDispatchToProps
)(ConsentWall);
